import React from 'react';

import type { TextFieldProps } from '@mui/material';
import { TextField } from '@mui/material';

import { PickerDialog } from './PickerDialog';

/**
 * 색상 선택 필드. `showPicker` 상태로 피커 표시 여부를 제어.
 *
 * ```tsx
 * const [color, setColor] = useState<string | undefined>(undefined);
 * const { value: showPicker, setValue: setShowPicker } = useBoolean(false);
 *
 * <ColorPicker
 *   value={color}
 *   showPicker={showPicker}
 *   setShowPicker={setShowPicker}
 *   onChange={setColor}
 * />
 * ```
 *
 * NOTE: 원본 코드가 MUI 4.0 이라서, 직접 포팅.
 * @see https://github.com/LoicMahieu/material-ui-color-picker
 */
export const ColorPicker = React.memo(function ColorPicker({
  onChange,
  disableAlpha,

  // Text field
  id,
  hintText,
  placeholder,
  floatingLabelText,
  label,
  TextFieldProps,
  value,

  // State
  showPicker = false,
  setShowPicker,
}: {
  value?: string;
  disableAlpha?: boolean;
  onChange: (color?: string) => void;
  id?: string;
  hintText?: string;
  placeholder?: string;
  label?: string;
  floatingLabelText?: string;
  TextFieldProps?: TextFieldProps;
  showPicker?: boolean;
  setShowPicker: (show: boolean) => void;
}) {
  return (
    <>
      <TextField
        id={id}
        label={floatingLabelText || label}
        placeholder={hintText || placeholder}
        onClick={() => setShowPicker(true)}
        onChange={e => {
          onChange(e.target.value);
        }}
        InputProps={{ style: { color: value } }}
        {...TextFieldProps}
      />
      {showPicker && (
        <PickerDialog
          color={value}
          disableAlpha={disableAlpha}
          onClick={() => {
            setShowPicker(false);
            onChange(value);
          }}
          onChange={c => {
            const result = c.rgb.a === 1 ? c.hex : `rgba(${c.rgb.r}, ${c.rgb.g}, ${c.rgb.b}, ${c.rgb.a})`;
            onChange(result);
          }}
        />
      )}
    </>
  );
});
